import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import { Plus, Trash2, ArrowLeft, FileText, ListChecks, Sparkles, Upload, Eye } from 'lucide-react';
import FilePreviewDialog from 'components/FilePreviewDialog';
import {
  getScenario,
  updateScenario,
  deleteScenario,
  listRoles,
  listLlmModels,
  getScenarioRoleId,
} from 'services/scenariosApi';
import type { BackendScenario, BackendRole, LlmModel } from 'services/scenariosApi';
import MarkdownEditor from 'components/MarkdownEditor';
import { uploadContextFile, deleteContextFile } from 'services/filesApi';
import type { ContextFile } from 'types';
import { ContextDocumentTargetType as TargetType } from 'types';
import { useAuthStore } from 'store/authStore';

const MAX_SCENARIO_FILES = 20;

interface FormState {
  title: string;
  shortDescription: string;
  roles: string[];
  steps: string[];
  result: string;
  instruction: string;
  system_prompt: string;
  model: string;
  minFiles: number;
  maxFiles: number;
}

function validate(form: FormState): string | null {
  if (!form.title.trim()) return 'Укажите название';
  if (!form.shortDescription.trim()) return 'Укажите краткое описание';
  if (form.roles.length === 0) return 'Выберите хотя бы одну роль';
  if (form.steps.every((s) => !s.trim())) return 'Добавьте хотя бы один шаг';
  if (!form.result.trim()) return 'Укажите ожидаемый результат';
  if (!form.instruction.trim()) return 'Укажите инструкцию';
  if (!form.system_prompt.trim()) return 'Укажите системный промпт для AI';
  if (form.maxFiles < form.minFiles) return 'Макс. файлов не может быть меньше мин.';
  return null;
}

function backendToForm(s: BackendScenario): FormState {
  return {
    title: s.title,
    shortDescription: s.description ?? '',
    roles: [getScenarioRoleId(s)].filter(Boolean),
    steps: s.action
      ? s.action
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean)
      : [''],
    result: s.result ?? '',
    instruction: s.instruction ?? '',
    system_prompt: s.system_prompt ?? '',
    model: s.model?.id ?? '',
    minFiles: s.min_files ?? 0,
    maxFiles: s.max_files ?? 5,
  };
}

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}

function Section({ icon, title, description, children }: SectionProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3.5 },
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (t) => `${t.palette.primary.main}14`,
            color: 'primary.main',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.2px' }}>
            {title}
          </Typography>
          {description && (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{description}</Typography>
          )}
        </Box>
      </Box>
      <Divider sx={{ my: 2.5 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>{children}</Box>
    </Paper>
  );
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 600,
        color: 'text.secondary',
        mb: 1,
        letterSpacing: 0.2,
      }}
    >
      {children}
      {required && ' *'}
    </Typography>
  );
}

export default function ScenarioEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [scenarioTitle, setScenarioTitle] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roles, setRoles] = useState<BackendRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [models, setModels] = useState<LlmModel[]>([]);

  const user = useAuthStore((s) => s.user);
  const projectId = user?.projectId ?? '';
  const [scenarioDocs, setScenarioDocs] = useState<ContextFile[]>([]);
  const [isScenarioUploading, setIsScenarioUploading] = useState(false);
  const [scenarioDeletingId, setScenarioDeletingId] = useState<string | null>(null);
  const [scenarioUploadError, setScenarioUploadError] = useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = useState<ContextFile | null>(null);
  const scenarioFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    listRoles()
      .then((data) => setRoles((data ?? []).filter((r) => r.name !== 'admin')))
      .finally(() => setRolesLoading(false));
    listLlmModels().then((data) => setModels(data ?? []));
  }, []);

  useEffect(() => {
    if (!id) return;
    getScenario(id)
      .then((data) => {
        if (!data) {
          setNotFound(true);
        } else {
          setScenarioTitle(data.title);
          setForm(backendToForm(data));
          setScenarioDocs(data.context_documents ?? []);
        }
      })
      .catch(() => setNotFound(true));
  }, [id]);

  const set = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  const toggleRole = useCallback((roleName: string) => {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        roles: prev.roles.includes(roleName)
          ? prev.roles.filter((r) => r !== roleName)
          : [...prev.roles, roleName],
      };
    });
  }, []);

  const setStep = useCallback((index: number, value: string) => {
    setForm((prev) => {
      if (!prev) return prev;
      const steps = [...prev.steps];
      steps[index] = value;
      return { ...prev, steps };
    });
  }, []);

  const addStep = useCallback(() => {
    setForm((prev) => (prev ? { ...prev, steps: [...prev.steps, ''] } : prev));
  }, []);

  const removeStep = useCallback((index: number) => {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        steps: prev.steps.length > 1 ? prev.steps.filter((_, i) => i !== index) : prev.steps,
      };
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!form || !id) return;

    const validationError = validate(form);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await updateScenario(id, {
        role_id: form.roles[0],
        title: form.title,
        description: form.shortDescription,
        action: form.steps.filter((s) => s.trim()).join('\n'),
        result: form.result,
        instruction: form.instruction,
        system_prompt: form.system_prompt,
        model: form.model || null,
        min_files: form.minFiles,
        max_files: form.maxFiles,
      });
      navigate('/scenarios');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения сценария');
    } finally {
      setIsSubmitting(false);
    }
  }, [form, id, navigate]);

  const handleScenarioFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!id || !projectId) return;
      const files = e.target.files ? Array.from(e.target.files) : [];
      e.target.value = '';
      if (files.length === 0) return;

      setIsScenarioUploading(true);
      setScenarioUploadError(null);
      try {
        for (const file of files) {
          const result = await uploadContextFile(
            projectId,
            { type: TargetType.Scenario, id },
            file
          );
          if (result.error) {
            setScenarioUploadError(result.error);
          }
        }
        const refreshed = await getScenario(id);
        if (refreshed) setScenarioDocs(refreshed.context_documents ?? []);
      } catch (err) {
        setScenarioUploadError(err instanceof Error ? err.message : 'Ошибка загрузки');
      } finally {
        setIsScenarioUploading(false);
      }
    },
    [id, projectId]
  );

  const handleScenarioFileDelete = useCallback(async (docId: string) => {
    setScenarioDeletingId(docId);
    try {
      await deleteContextFile(docId);
      setScenarioDocs((prev) => prev.filter((d) => d.id !== docId));
    } finally {
      setScenarioDeletingId(null);
    }
  }, []);

  const handleDelete = useCallback(async () => {
    if (!id) return;
    setIsDeleting(true);
    setError(null);
    try {
      await deleteScenario(id);
      navigate('/scenarios');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка удаления сценария');
    } finally {
      setIsDeleting(false);
    }
  }, [id, navigate]);

  if (notFound) {
    return (
      <Box sx={{ p: { xs: 2, md: '32px 48px' }, maxWidth: 1100, mx: 'auto', width: '100%' }}>
        <Alert severity="error">Сценарий не найден.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/scenarios')}>
          Назад к сценариям
        </Button>
      </Box>
    );
  }

  if (!form) {
    return (
      <Box sx={{ p: { xs: 2, md: '32px 48px' }, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 12, bgcolor: 'background.default', minHeight: '100%' }}>
      <Box
        sx={{
          p: { xs: 2, md: '32px 48px' },
          maxWidth: 1100,
          mx: 'auto',
          width: '100%',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 4,
          }}
        >
          <IconButton
            onClick={() => navigate('/scenarios')}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              width: 40,
              height: 40,
            }}
          >
            <ArrowLeft size={18} />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: 26,
                fontWeight: 800,
                letterSpacing: '-0.5px',
                color: 'text.primary',
                lineHeight: 1.2,
              }}
            >
              Редактировать сценарий
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5 }}>
              {scenarioTitle}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Section 1: Basic Info */}
          <Section
            icon={<FileText size={18} />}
            title="Основная информация"
            description="Название, описание и кому доступен сценарий"
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2.5,
              }}
            >
              <TextField
                label="Название"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Краткое описание"
                value={form.shortDescription}
                onChange={(e) => set('shortDescription', e.target.value)}
                fullWidth
                required
              />
            </Box>

            <Box>
              <FieldLabel required>Роли</FieldLabel>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {rolesLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  roles.map((role) => {
                    const active = form.roles.includes(role.id);
                    return (
                      <Chip
                        key={role.id}
                        label={role.title ?? role.name}
                        onClick={() => toggleRole(role.id)}
                        color={active ? 'primary' : 'default'}
                        variant={active ? 'filled' : 'outlined'}
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 500,
                          px: 0.5,
                          height: 32,
                        }}
                      />
                    );
                  })
                )}
              </Box>
            </Box>
          </Section>

          {/* Section 2: Scenario */}
          <Section
            icon={<ListChecks size={18} />}
            title="Сценарий"
            description="Что делает пользователь и какой ожидается результат"
          >
            <Box>
              <FieldLabel required>Шаги сценария</FieldLabel>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {form.steps.map((step, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        flexShrink: 0,
                        borderRadius: '50%',
                        bgcolor: (t) => `${t.palette.primary.main}14`,
                        color: 'primary.main',
                        fontSize: 12,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {i + 1}
                    </Box>
                    <TextField
                      value={step}
                      onChange={(e) => setStep(i, e.target.value)}
                      placeholder={`Шаг ${i + 1}`}
                      fullWidth
                    />
                    <IconButton
                      onClick={() => removeStep(i)}
                      disabled={form.steps.length === 1}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': { color: '#ef4444', bgcolor: '#ef444410' },
                      }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<Plus size={16} />}
                  onClick={addStep}
                  variant="outlined"
                  sx={{ alignSelf: 'flex-start', mt: 1, borderRadius: 2 }}
                >
                  Добавить шаг
                </Button>
              </Box>
            </Box>

            <TextField
              label="Ожидаемый результат"
              value={form.result}
              onChange={(e) => set('result', e.target.value)}
              fullWidth
              required
              multiline
              minRows={2}
            />

            <TextField
              label="Инструкция для пользователя"
              value={form.instruction}
              onChange={(e) => set('instruction', e.target.value)}
              fullWidth
              required
              multiline
              minRows={3}
              helperText="Подсказка пользователю — что нужно загрузить и как использовать сценарий"
            />
          </Section>

          {/* Section 3: AI */}
          <Section
            icon={<Sparkles size={18} />}
            title="Настройки AI"
            description="Промпт, модель и ограничения по файлам"
          >
            <MarkdownEditor
              label="Системный промпт для AI"
              value={form.system_prompt}
              onChange={(v) => set('system_prompt', v)}
              required
              height={360}
              helperText="Поддерживается Markdown: заголовки, списки, таблицы, код"
            />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' },
                gap: 2.5,
              }}
            >
              <FormControl fullWidth>
                <InputLabel>Модель AI</InputLabel>
                <Select
                  value={form.model}
                  label="Модель AI"
                  onChange={(e) => set('model', e.target.value)}
                >
                  <MenuItem value="">
                    <em>По умолчанию</em>
                  </MenuItem>
                  {models.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Мин. файлов"
                type="number"
                value={form.minFiles}
                onChange={(e) =>
                  set('minFiles', Math.max(0, Math.min(MAX_SCENARIO_FILES, Number(e.target.value))))
                }
                inputProps={{ min: 0, max: MAX_SCENARIO_FILES }}
              />
              <TextField
                label="Макс. файлов"
                type="number"
                value={form.maxFiles}
                onChange={(e) =>
                  set('maxFiles', Math.max(1, Math.min(MAX_SCENARIO_FILES, Number(e.target.value))))
                }
                inputProps={{ min: 1, max: MAX_SCENARIO_FILES }}
              />
            </Box>

            {/* Scenario files subsection */}
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <FieldLabel>Предзаданные файлы сценария</FieldLabel>
                <Box
                  onClick={() => !isScenarioUploading && scenarioFileInputRef.current?.click()}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.625,
                    fontSize: 12,
                    fontWeight: 600,
                    color: isScenarioUploading ? 'text.disabled' : 'primary.main',
                    bgcolor: isScenarioUploading
                      ? 'action.disabledBackground'
                      : (t: any) => `${t.palette.primary.main}14`,
                    border: '1px solid',
                    borderColor: isScenarioUploading
                      ? 'divider'
                      : (t: any) => `${t.palette.primary.main}40`,
                    borderRadius: 1.5,
                    cursor: isScenarioUploading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': isScenarioUploading
                      ? {}
                      : { bgcolor: (t: any) => `${t.palette.primary.main}22` },
                  }}
                >
                  {isScenarioUploading ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <Upload size={13} />
                  )}
                  {isScenarioUploading ? 'Загрузка...' : 'Загрузить файл'}
                </Box>
              </Box>
              <input
                ref={scenarioFileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                accept=".pdf,.docx,.doc,.txt,.xlsx,.csv"
                onChange={handleScenarioFileUpload}
              />
              {scenarioUploadError && (
                <Alert
                  severity="error"
                  sx={{ mb: 1.5, borderRadius: 1.5 }}
                  onClose={() => setScenarioUploadError(null)}
                >
                  {scenarioUploadError}
                </Alert>
              )}
              {scenarioDocs.length === 0 ? (
                <Box
                  sx={{
                    border: '1.5px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    py: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    color: 'text.disabled',
                    fontSize: 13,
                  }}
                >
                  <FileText size={16} />
                  Файлы не добавлены
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {scenarioDocs.map((doc) => {
                    const isDeleting = scenarioDeletingId === doc.id;
                    return (
                      <Box
                        key={doc.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          px: 1.5,
                          py: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          bgcolor: 'background.default',
                          opacity: isDeleting ? 0.5 : 1,
                        }}
                      >
                        <FileText size={15} style={{ flexShrink: 0, color: '#6b7280' }} />
                        <Typography
                          sx={{
                            flex: 1,
                            fontSize: 13,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'text.primary',
                          }}
                        >
                          {doc.name}
                        </Typography>
                        {doc.file?.size && (
                          <Typography sx={{ fontSize: 11, color: 'text.disabled', flexShrink: 0 }}>
                            {Math.round(doc.file.size / 1024)} КБ
                          </Typography>
                        )}
                        <Box
                          onClick={() => setPreviewDoc(doc)}
                          title="Просмотреть"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            flexShrink: 0,
                            cursor: 'pointer',
                            color: 'text.secondary',
                            '&:hover': {
                              bgcolor: (t: any) => `${t.palette.primary.main}14`,
                              color: 'primary.main',
                            },
                            transition: 'all 0.12s',
                          }}
                        >
                          <Eye size={14} />
                        </Box>
                        <Box
                          onClick={() => !isDeleting && handleScenarioFileDelete(doc.id)}
                          title="Удалить"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            flexShrink: 0,
                            cursor: isDeleting ? 'not-allowed' : 'pointer',
                            color: 'text.secondary',
                            '&:hover': isDeleting ? {} : { bgcolor: '#ef444410', color: '#ef4444' },
                            transition: 'all 0.12s',
                          }}
                        >
                          {isDeleting ? (
                            <CircularProgress size={12} color="inherit" />
                          ) : (
                            <Trash2 size={14} />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Section>
        </Box>
      </Box>

      {/* Sticky Footer */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          py: 2,
          px: { xs: 2, md: 6 },
          backdropFilter: 'blur(8px)',
          zIndex: 10,
        }}
      >
        <Box
          sx={{
            maxWidth: 1100,
            mx: 'auto',
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting || isDeleting}
            startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ minWidth: 200, py: 1.1, borderRadius: 2, fontWeight: 600 }}
          >
            {isSubmitting ? 'Сохранение...' : 'Сохранить изменения'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/scenarios')}
            disabled={isSubmitting || isDeleting}
            sx={{ borderRadius: 2 }}
          >
            Отмена
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDelete}
            disabled={isSubmitting || isDeleting}
            startIcon={
              isDeleting ? <CircularProgress size={14} color="inherit" /> : <Trash2 size={16} />
            }
            sx={{ ml: 'auto', borderRadius: 2 }}
          >
            {isDeleting ? 'Удаление...' : 'Удалить сценарий'}
          </Button>
        </Box>
      </Box>

      <FilePreviewDialog
        open={!!previewDoc}
        documentId={previewDoc?.id ?? null}
        fileName={previewDoc?.name ?? ''}
        mimeType={previewDoc?.file?.mime_type ?? null}
        onClose={() => setPreviewDoc(null)}
      />
    </Box>
  );
}
